import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import usePendingPurchases from '../hooks/usePendingPurchases';
import { deletePendingPurchase } from '../api';
import CancelPendingSaleModal from './CancelPendingSaleModal';
import DocumentTabs from './DocumentTabs';
import { notify } from '../utils/alertBus';
import { filterPendingByRegisterScope } from '../utils/pendingPurchases';

const PendingSalesTabs = ({
  branchId,
  activePurchaseId,
  onSelect,
  cashRegisterId,
  cashRegisterSessionId,
  maxOpenSales,
  enableRealtime = true,
  tone = 'neutral',
  canDelete = true,
  canCreate = true,
  canSelect = true,
  scopeReady = true,
  activePurchaseLiveId = null,
  activePurchaseLiveTotal = null,
}) => {
  const { pending, loading, error, reload } = usePendingPurchases(branchId, {
    enableRealtime,
  });
  const [cancelTarget, setCancelTarget] = useState(null);

  const handleSelect = (idOrNull) => {
    if (idOrNull && !canSelect) return;
    if (!idOrNull && !canCreate) return;
    if (typeof onSelect === 'function') {
      onSelect(idOrNull);
    }
  };

  const handleDelete = (purchase) => {
    if (!canDelete) return;
    if (!branchId || !purchase?.id) return;
    setCancelTarget(purchase);
  };

  const handleConfirmDelete = async (cancelPayload) => {
    if (!canDelete) return;
    if (!branchId || !cancelTarget?.id) {
      throw new Error('No se pudo identificar la venta.');
    }
    try {
      await deletePendingPurchase(branchId, cancelTarget.id, cancelPayload || {});
      if (cancelTarget.id === activePurchaseId) {
        handleSelect(null);
      }
      await reload();
    } catch (err) {
      console.error('Error deleting pending purchase:', err);
      throw new Error(
        err?.response?.data?.message || 'No se pudo eliminar la venta pendiente.'
      );
    }
  };

  const formatLabel = (purchase) => {
    const clientName = purchase.Client?.name || 'Sin cliente';
    const total =
      typeof purchase.totalAmount === 'number'
        ? `$${purchase.totalAmount.toLocaleString('de-DE')}`
        : '';

    let time = '';
    if (purchase.createdAt) {
      const created = new Date(purchase.createdAt);
      if (!Number.isNaN(created.getTime())) {
        time = format(created, 'HH:mm');
      }
    }

    return [clientName, total || null, time || null]
      .filter(Boolean)
      .join(' · ');
  };

  const isNewActive = !activePurchaseId;
  const parsedMaxOpenSales = Number.isInteger(maxOpenSales) ? maxOpenSales : 5;
  const limitApplies =
    (cashRegisterSessionId !== null && cashRegisterSessionId !== undefined)
    || (cashRegisterId !== null && cashRegisterId !== undefined);
  const pendingByStatus = useMemo(
    () =>
      (pending || []).filter((purchase) =>
        purchase?.saleStatus === 'open' || purchase?.saleStatus === 'suspended'
      ),
    [pending]
  );

  const scopedPending = useMemo(
    () => {
      if (!scopeReady) return [];
      return filterPendingByRegisterScope(pendingByStatus, {
        cashRegisterSessionId,
        cashRegisterId,
      });
    },
    [pendingByStatus, cashRegisterSessionId, cashRegisterId, scopeReady]
  );
  const displayPending = useMemo(() => {
    const livePurchaseId = activePurchaseLiveId == null ? null : String(activePurchaseLiveId);
    const liveTotal = Number(activePurchaseLiveTotal);

    return scopedPending.map((purchase) => {
      return {
        ...purchase,
        ...(
          livePurchaseId !== null
          && String(purchase?.id) === livePurchaseId
          && Number.isFinite(liveTotal)
            ? { totalAmount: liveTotal }
            : {}
        ),
      };
    });
  }, [activePurchaseLiveId, activePurchaseLiveTotal, scopedPending]);
  const openSales = displayPending.filter((purchase) => purchase.saleStatus === 'open');
  const shouldBlockNewSale =
    limitApplies && !isNewActive && openSales.length >= parsedMaxOpenSales;
  const newSaleBlockedMessage = `Se alcanzó el límite de ventas pendientes (${parsedMaxOpenSales}) para esta caja.`;

  const handleNewSaleClick = () => {
    if (shouldBlockNewSale) {
      notify(newSaleBlockedMessage);
      return;
    }
    handleSelect(null);
  };

  const buildTabTitle = (purchase) => {
    const type = (purchase?.documentType || '').toLowerCase();
    const typeLabel = type === 'boleta' || type === 'factura'
      ? `${type.charAt(0).toUpperCase()}${type.slice(1)} `
      : '';
    return `${typeLabel}#${purchase.id}`;
  };

  return (
    <>
      <DocumentTabs
        tabs={displayPending}
        activeId={activePurchaseId}
        activeNew={isNewActive}
        onSelect={(purchase) => handleSelect(purchase.id)}
        onNew={handleNewSaleClick}
        onDelete={handleDelete}
        canCreate={canCreate}
        canSelect={canSelect}
        canDelete={canDelete}
        loading={loading}
        error={error}
        scopeReady={scopeReady}
        scopeLoadingLabel="Cargando pendientes"
        tone={tone}
        newTitle="Nueva venta"
        newDisabled={shouldBlockNewSale}
        newDisabledMessage={newSaleBlockedMessage}
        listTitle="Ver pendientes"
        menuTitle="Pendientes"
        emptyMessage="No hay pendientes hoy."
        getTabTitle={buildTabTitle}
        getTabMeta={formatLabel}
      />
      <CancelPendingSaleModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        saleLabel={cancelTarget ? buildTabTitle(cancelTarget) : ''}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default PendingSalesTabs;
